import React from 'react';
import { Link } from 'gatsby';
import type { GatsbyLinkProps } from 'gatsby';
import { getLocalizedPath, usePageContext } from 'gatsby-theme-localized';
import type { RulesExtend } from '@ackee/fela';
import { useFelaEnhanced } from '@ackee/fela';

import { languages } from '../../../gatsby-theme-localized/i18n/config/languages';

import * as felaRules from './LocalizedLink.rules';

export interface LocalizedLinkProps extends Omit<GatsbyLinkProps<any>, 'ref'> {
    to: string;
    activeStyle?: object;
    handleCarouselHover?: (hovering: boolean) => void;
    anchor?: string;
    eventId?: string;
    extend?: RulesExtend<typeof felaRules>;
    params?: Record<string, string>;
}

export const LocalizedLink = ({
    to,
    activeStyle,
    handleCarouselHover,
    anchor,
    eventId,
    params,
    extend,
    ...props
}: LocalizedLinkProps) => {
    const { styles } = useFelaEnhanced(felaRules, { extend });
    const { locale } = usePageContext();

    const localizedTo = getLocalizedPath(to, locale, languages, true);

    const parsedParams = params ? new URLSearchParams(params).toString() : undefined;

    const finalUrl = parsedParams ? `${localizedTo}?${parsedParams}` : localizedTo;

    if (handleCarouselHover) {
        return (
            <Link
                onMouseEnter={() => handleCarouselHover(false)}
                onMouseLeave={() => handleCarouselHover(true)}
                activeStyle={activeStyle}
                className={styles.link}
                to={finalUrl}
                data-event-id={eventId}
                {...props}
            />
        );
    }

    if (anchor) {
        return (
            <Link
                to={`${localizedTo + anchor}`}
                activeStyle={activeStyle}
                className={styles.link}
                data-event-id={eventId}
                {...props}
            />
        );
    }

    return <Link activeStyle={activeStyle} className={styles.link} to={finalUrl} data-event-id={eventId} {...props} />;
};
